// Los dos rankings del Home: tiendas y vendedores. Reusan los datos de sus pantallas y enlazan a
// ellas por deep link — el tablero es una puerta, no una copia.
//
// Las cinco columnas son las del mockup: posición · quién · piezas · monto · avance de meta. La
// última se pinta vacía cuando el target no tiene cuota fijada; un 0% diría que no vendió nada.
import { Link } from '@tanstack/react-router';
import { ErrorState, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import type { SellerKpiRow, StoreKpiRow } from '@/api/services/retail-org/backoffice';
import { deltaDirection, formatDelta } from '../../tiendas/lib/tiendasFilters';
import { sortSellers } from '../../vendedores/lib/sellerRanking';
import { formatMoneyShort } from '../lib/homeMetrics';
import { ProgressMini } from './ProgressMini';

const MXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

/** `null` es "no sé" y se pinta "—". Un `$0` diría "no vendió", que es otra cosa. */
const moneyShort = formatMoneyShort;

/** Para ORDENAR sí se puede tratar el hueco como 0 — es una decisión de orden, no un dato a la vista. */
const forSort = (cents: number | null) => cents ?? 0;

const DELTA_CLASS = {
    up: 'bg-success-soft text-success',
    down: 'bg-destructive/10 text-destructive',
    flat: 'bg-muted text-muted-foreground',
    unknown: 'bg-muted text-muted-foreground',
} as const;

const GRID = 'grid grid-cols-[20px_1fr_44px_64px_62px] items-center gap-1.5';
const GOAL_NO_SOURCE = 'Este target no tiene cuota fijada para el periodo.';
/** El mockup pone el código de vendedor (`VL-0007`) junto a su tienda; el silo manda un ULID. */
const SELLER_CODE_NO_SOURCE =
    'El mockup muestra el código del vendedor junto a su tienda. El silo manda un identificador interno, no ese código.';

/** Avance contra la cuota del día. `null` sin cuota: no se cae a 0%, que se leería como "no vendió". */
function goalProgress(soldCents: number | null, goalCents: number | null): number | null {
    if (goalCents === null || goalCents <= 0) return null;
    return (soldCents ?? 0) / goalCents;
}

/** La celda de meta del mockup: barrita + porcentaje. Sin cuota queda la barra vacía y un guion. */
function GoalCell({ pct }: { pct: number | null }) {
    return (
        <span className="flex items-center gap-1" title={pct === null ? GOAL_NO_SOURCE : undefined}>
            <ProgressMini pct={pct} className="w-auto flex-1" />
            <span className="text-[10px] font-bold tabular-nums">
                {pct === null ? '—' : `${String(Math.round(pct * 100))}%`}
            </span>
        </span>
    );
}

/** Número arriba y su unidad abajo, como las celdas de `piezas` y `monto` del mockup. */
function StackedCell({
    value,
    sub,
    className,
}: {
    value: React.ReactNode;
    sub: React.ReactNode;
    className?: string;
}) {
    return (
        <span className={cn('text-right font-semibold tabular-nums', className)}>
            {value}
            <span className="block text-right text-[9.5px] font-normal text-muted-foreground">
                {sub}
            </span>
        </span>
    );
}

/**
 * El renglón de contexto de una tienda: zona, vendedores y si es subdistribuidor.
 * Hoy el silo manda los tres en `null`, así que la fila lo dice en vez de quedarse muda.
 */
function storeSubtitle(row: StoreKpiRow): string {
    const parts: string[] = [];
    if (row.zone) parts.push(row.zone);
    if (row.activeSellerCount !== null) parts.push(`${String(row.activeSellerCount)} vend`);
    if (row.kind === 'SUBDISTRIBUTOR') parts.push('Indep.');
    return parts.length > 0 ? parts.join(' · ') : 'Zona y vendedores sin fuente';
}

export function HomeRankings({
    kind,
    stores,
    sellers,
    isLoading,
    error,
    onRetry,
}: {
    kind: 'stores' | 'sellers';
    stores?: StoreKpiRow[];
    sellers?: SellerKpiRow[];
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
}) {
    if (error) {
        return (
            <ErrorState
                className="m-2"
                title={
                    kind === 'stores'
                        ? 'No se pudo cargar el ranking de tiendas'
                        : 'No se pudo cargar el ranking de vendedores'
                }
                description={error.message}
                onRetry={onRetry}
            />
        );
    }
    if (isLoading) {
        return (
            <div className="p-3">
                <Skeleton lines={6} />
            </div>
        );
    }

    if (kind === 'stores') {
        const rows = [...(stores ?? [])].sort(
            (a, b) => forSort(b.soldTodayCents) - forSort(a.soldTodayCents),
        );
        if (rows.length === 0) {
            return (
                <div className="py-6 text-center text-[11px] text-muted-foreground">
                    Todavía no hay ventas hoy.
                </div>
            );
        }
        return (
            <div className="px-2 py-1">
                {rows.map((row, i) => {
                    const dir = deltaDirection(row.weekOverWeekChange);
                    return (
                        <Link
                            key={row.storeId}
                            to="/retail-admin/$section"
                            params={{ section: 'tiendas' }}
                            search={{ tienda: row.storeId }}
                            className={cn(
                                GRID,
                                'rounded px-1 py-1 text-[11px] transition-colors hover:bg-accent/50',
                            )}
                        >
                            <span className="text-[10px] text-muted-foreground tabular-nums">
                                {i + 1}
                            </span>
                            <div className="min-w-0">
                                <div className="truncate font-medium">{row.name}</div>
                                <div className="truncate text-[9.5px] text-muted-foreground">
                                    {storeSubtitle(row)}
                                </div>
                            </div>
                            <StackedCell value={row.unitsToday ?? '—'} sub="piezas" />
                            <StackedCell
                                value={moneyShort(row.soldTodayCents)}
                                sub={
                                    <span
                                        className={cn(
                                            'rounded px-1 py-px text-[9.5px] font-bold tabular-nums',
                                            DELTA_CLASS[dir],
                                        )}
                                    >
                                        {formatDelta(row.weekOverWeekChange)}
                                    </span>
                                }
                            />
                            <GoalCell pct={goalProgress(row.soldTodayCents, row.dailyGoalCents)} />
                        </Link>
                    );
                })}
            </div>
        );
    }

    // El mockup corta el ranking de vendedores en 10 — el Home es un resumen, no la pantalla.
    const rows = sortSellers(sellers ?? []).slice(0, 10);
    if (rows.length === 0) {
        return (
            <div className="py-6 text-center text-[11px] text-muted-foreground">
                Todavía no hay ventas hoy.
            </div>
        );
    }
    return (
        <div className="px-2 py-1">
            {rows.map((row, i) => (
                <Link
                    key={row.sellerId}
                    to="/retail-admin/$section"
                    params={{ section: 'vendedores' }}
                    search={{ vendedor: row.sellerId }}
                    className={cn(
                        GRID,
                        'rounded px-1 py-1 text-[11px] transition-colors hover:bg-accent/50',
                    )}
                >
                    <span className="text-[10px] text-muted-foreground tabular-nums">{i + 1}</span>
                    <div className="min-w-0">
                        <div className="truncate font-medium">
                            {row.name}
                            {row.isStoreManager === true ? ' ★' : ''}
                        </div>
                        <div
                            className="truncate text-[9.5px] text-muted-foreground"
                            title={SELLER_CODE_NO_SOURCE}
                        >
                            {row.storeName ?? 'Tienda sin fuente'}
                        </div>
                    </div>
                    <StackedCell value={row.unitsToday ?? '—'} sub="piezas" />
                    <StackedCell
                        value={moneyShort(row.soldTodayCents)}
                        sub={
                            row.dailyGoalCents === null ? (
                                <span title={GOAL_NO_SOURCE}>de —</span>
                            ) : (
                                `de ${moneyShort(row.dailyGoalCents)}`
                            )
                        }
                    />
                    <GoalCell pct={goalProgress(row.soldTodayCents, row.dailyGoalCents)} />
                </Link>
            ))}
        </div>
    );
}

export { MXN };
